/* ORION: the site header's behaviour (sections/orion-figma-homepage.liquid, assets/orion-header.css; owner, 2026-10-01):
   - it slides away while the page goes down and comes back, as glass, when it goes up (never while something of it is
     open), and tells the page how much of the top it covers (--orion-header-offset on the root) so that parts that stay
     on screen can move below it;
   - a thin light runs along its foot when the page opens and whenever it comes back;
   - the sleep products' list opens under its word (mouse, tap, keyboard) and the languages under the globe;
   - on narrow screens the menu button opens the links full screen, with the sleep products' list sliding in beside them.
   By the owner's rule all of it plays also when the system asks for reduced motion. */
(() => {
  const html = document.documentElement;
  const narrow = window.matchMedia('(max-width: 1199px)');
  const canHover = window.matchMedia('(hover: hover)');

  function header(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const holder = root.parentElement;
    holder.classList.add('orion-header-holder');
    const bar = root.querySelector('.orion-header__bar');
    let hidden = false, lastY = window.scrollY, frame = 0, menuOpen = false, barHeight = 0, started = false;

    // ------------------------------------------------------------------------------------------- Away and back
    // The bar's height comes from a ResizeObserver, after the page's own layout: read from a script at start it forced
    // the whole first layout into this file (owner, 2026-10-01: a fast homepage on phones).
    const coverHeight = () => barHeight || 60;
    const setOffset = () => html.style.setProperty('--orion-header-offset', hidden ? '0px' : `${coverHeight()}px`);
    const sweep = () => {
      root.classList.remove('is-sweep');
      // Restarted two frames on, so the light never forces a layout either.
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-sweep')));
    };
    root.addEventListener('animationend', (event) => { if (event.animationName === 'orion-header-sweep') root.classList.remove('is-sweep'); });
    const busy = () => menuOpen || Boolean(root.querySelector('.orion-header__item.is-open, .orion-header__langs.is-open, details[open]'));
    const setHidden = (value) => {
      if (value === hidden) return;
      hidden = value;
      holder.classList.toggle('is-hidden', hidden);
      if (!hidden) sweep();
      setOffset();
    };
    const onScroll = () => {
      frame = 0;
      const y = Math.max(0, window.scrollY);
      const step = y - lastY;
      root.classList.toggle('is-scrolled', y > 2);
      if (y < coverHeight() + 40 || busy()) setHidden(false);
      else if (step > 6) setHidden(true);
      else if (step < -6) setHidden(false);
      if (Math.abs(step) > 6 || y < 2) lastY = y;
    };
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(onScroll); }, { passive: true });
    // The keyboard reaching the header brings it back.
    root.addEventListener('focusin', () => setHidden(false));
    new ResizeObserver(() => {
      barHeight = Math.round(bar.getBoundingClientRect().height);
      setOffset();
      if (!started) { started = true; onScroll(); }
    }).observe(bar);
    setTimeout(sweep, 500);

    // ------------------------------------------------------------------------------------------- The sleep products' list
    const drops = [...root.querySelectorAll('[data-orion-header-drop]')].map((item) => {
      const trigger = item.querySelector('.orion-header__link');
      let timer = 0;
      const open = () => {
        clearTimeout(timer);
        closeAll(item);
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        clearTimeout(timer);
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };
      item.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { clearTimeout(timer); timer = setTimeout(open, 70); } });
      item.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') { clearTimeout(timer); timer = setTimeout(close, 170); } });
      // Without a mouse the first tap opens the list and the second follows the link.
      trigger.addEventListener('click', (event) => {
        if (canHover.matches || item.classList.contains('is-open')) return;
        event.preventDefault();
        open();
      });
      item.addEventListener('focusin', (event) => { if (event.target !== trigger || trigger.matches(':focus-visible')) open(); });
      item.addEventListener('focusout', (event) => { if (!item.contains(event.relatedTarget)) close(); });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && item.classList.contains('is-open')) { close(); trigger.focus(); }
        if (event.key === 'ArrowDown') { event.preventDefault(); open(); item.querySelector('.orion-header__drop a')?.focus(); }
      });
      return { item, close };
    });

    // ------------------------------------------------------------------------------------------- The languages
    const langs = root.querySelector('[data-orion-header-langs]');
    const langButton = langs && langs.querySelector('.orion-header__lang');
    const setLangs = (on) => {
      if (!langs) return;
      if (on) closeAll(langs);
      langs.classList.toggle('is-open', on);
      langButton.setAttribute('aria-expanded', String(on));
    };
    if (langs) {
      langButton.addEventListener('click', () => {
        const on = !langs.classList.contains('is-open');
        setLangs(on);
        if (on) setTimeout(() => langs.querySelector('.orion-header__langs-drop button:not([disabled])')?.focus({ preventScroll: true }), 60);
      });
      langs.addEventListener('keydown', (event) => { if (event.key === 'Escape' && langs.classList.contains('is-open')) { setLangs(false); langButton.focus(); } });
      langs.addEventListener('focusout', (event) => { if (!langs.contains(event.relatedTarget)) setLangs(false); });
    }

    // ------------------------------------------------------------------------------------------- The smart search
    // Its script, styles and data (assets/orion-smart-search.js) come only when the visitor reaches for the search
    // (owner, 2026-10-13: nothing heavy on the homepage): the mouse on the button, a touch, the keyboard, or the window
    // already open when this runs.
    const smart = root.querySelector('[data-orion-smart-script]');
    if (smart) {
      let asked = false;
      const ask = () => {
        if (asked) return;
        asked = true;
        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = smart.dataset.orionSmartStyle;
        document.head.appendChild(style);
        const script = document.createElement('script');
        script.src = smart.dataset.orionSmartScript;
        script.async = true;
        document.head.appendChild(script);
      };
      const button = smart.querySelector('summary');
      ['pointerenter', 'pointerdown', 'touchstart', 'focus'].forEach((type) => button?.addEventListener(type, ask, { once: true, passive: true }));
      smart.addEventListener('focusin', ask, { once: true });
      if (smart.querySelector('details[open]')) ask();
    }

    function closeAll(except) {
      drops.forEach((drop) => { if (drop.item !== except) drop.close(); });
      if (langs && langs !== except) setLangs(false);
    }
    document.addEventListener('pointerdown', (event) => { if (!root.contains(event.target)) closeAll(null); });

    // ------------------------------------------------------------------------------------------- The menu on narrow screens
    const burger = root.querySelector('[data-orion-header-burger]');
    const menu = root.querySelector('[data-orion-header-menu]');
    if (burger && menu) {
      const subOpen = menu.querySelector('[data-orion-header-sub-open]');
      const subBack = menu.querySelector('[data-orion-header-sub-back]');
      const sub = menu.querySelector('[data-orion-header-sub]');
      const main = menu.querySelector('[data-orion-header-main]');
      let closing = 0;
      const setSub = (on, focus) => {
        menu.classList.toggle('is-sub', on);
        subOpen?.setAttribute('aria-expanded', String(on));
        sub?.setAttribute('aria-hidden', String(!on));
        main?.setAttribute('aria-hidden', String(on));
        if (focus) setTimeout(() => (on ? subBack : subOpen)?.focus({ preventScroll: true }), 300);
      };
      const openMenu = () => {
        clearTimeout(closing);
        closeAll(null);
        menuOpen = true;
        setHidden(false);
        menu.hidden = false;
        html.classList.add('orion-header-locked');
        burger.setAttribute('aria-expanded', 'true');
        burger.setAttribute('aria-label', burger.dataset.labelClose);
        requestAnimationFrame(() => requestAnimationFrame(() => menu.classList.add('is-open')));
      };
      const closeMenu = (focus) => {
        if (!menuOpen) return;
        menuOpen = false;
        menu.classList.remove('is-open');
        burger.setAttribute('aria-expanded', 'false');
        burger.setAttribute('aria-label', burger.dataset.labelOpen);
        html.classList.remove('orion-header-locked');
        closing = setTimeout(() => { menu.hidden = true; setSub(false, false); }, 360);
        if (focus) burger.focus();
      };
      burger.addEventListener('click', () => (menuOpen ? closeMenu(false) : openMenu()));
      subOpen?.addEventListener('click', () => setSub(true, true));
      subBack?.addEventListener('click', () => setSub(false, true));
      menu.addEventListener('click', (event) => { if (event.target.closest('a')) closeMenu(false); });
      root.querySelector('.orion-header__search summary')?.addEventListener('click', () => closeMenu(false));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !menuOpen) return;
        if (menu.classList.contains('is-sub')) setSub(false, true); else closeMenu(true);
      });
      narrow.addEventListener('change', (event) => { if (!event.matches) closeMenu(false); });
      window.addEventListener('pageshow', () => closeMenu(false));
    }

    // Coming back to a page from the browser's memory: nothing left open, the header in place.
    window.addEventListener('pageshow', () => { closeAll(null); setHidden(false); lastY = window.scrollY; });
  }

  document.querySelectorAll('[data-orion-header]').forEach(header);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-orion-header]').forEach(header));
})();
